import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";

const FONT = `"Space Grotesk", system-ui, sans-serif`;
const UP = new THREE.Vector3(0, 1, 0), ONE = new THREE.Vector3(1, 1, 1);
const FACE = new THREE.Quaternion().setFromAxisAngle(UP, Math.PI / 4);

// A flag on a pole beside a house, in the colour its owner paid for: one instanced mesh for the poles (h is how tall),
// one for the cloths, which take the flag's colour. The cloth flies toward the default camera's right.
const POLE = new THREE.BoxGeometry(0.014, 1, 0.014).translate(0, 0.5, 0);
const CLOTH = new THREE.BoxGeometry(0.2, 0.12, 0.012).translate(0.1, 0, 0);
export function Flags({ flags }) {
  const pole = useRef(), cloth = useRef();
  useEffect(() => {
    const m = new THREE.Matrix4(), p = new THREE.Vector3(), s = new THREE.Vector3(), c = new THREE.Color(), none = new THREE.Quaternion();
    flags.forEach((f, i) => {
      pole.current.setMatrixAt(i, m.compose(p.set(f.x, 0.02, f.z), none, s.set(1, f.h, 1)));
      cloth.current.setMatrixAt(i, m.compose(p.set(f.x, 0.02 + f.h - 0.08, f.z), FACE, ONE));
      cloth.current.setColorAt(i, c.set(f.color));
    });
    for (const mesh of [pole.current, cloth.current]) {
      mesh.instanceMatrix.needsUpdate = true;
      mesh.computeBoundingSphere();
    }
    cloth.current.instanceColor.needsUpdate = true;
  }, [flags]);
  return (
    <>
      <instancedMesh ref={pole} args={[POLE, null, flags.length]} castShadow raycast={() => null}>
        <meshLambertMaterial color="#334155" />
      </instancedMesh>
      <instancedMesh ref={cloth} args={[CLOTH, null, flags.length]} castShadow raycast={() => null}>
        <meshLambertMaterial />
      </instancedMesh>
    </>
  );
}

// The text on a modern/placard (a lot's FOR RENT, a house's sign): bg, ink and up to two lines, the font shrunk to fit.
const W = 0.68, H = 0.26, PX = 640;
const TEXTS = new Map();
function plateTexture([text, bg, ink]) {
  const key = `${text}|${bg}|${ink}`;
  if (!TEXTS.has(key)) {
    const h = Math.round((PX * H) / W), canvas = document.createElement("canvas"), ctx = canvas.getContext("2d");
    canvas.width = PX;
    canvas.height = h;
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, PX, h);
    const cut = text.length > 10 ? text.lastIndexOf(" ", text.length / 2) + 1 || Math.ceil(text.length / 2) : 0;
    const lines = cut ? [text.slice(0, cut).trim(), text.slice(cut).trim()] : [text];
    let size = lines.length > 1 ? 100 : 130;
    ctx.font = `800 ${size}px ${FONT}`;
    while (Math.max(...lines.map((l) => ctx.measureText(l).width)) > PX * 0.92 && size > 12) ctx.font = `800 ${(size -= 4)}px ${FONT}`;
    ctx.fillStyle = ink;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    lines.forEach((l, i) => ctx.fillText(l, PX / 2, h / 2 + (i - (lines.length - 1) / 2) * size * 1.1 + size * 0.04));
    const map = new THREE.CanvasTexture(canvas);
    map.colorSpace = THREE.SRGBColorSpace;
    map.anisotropy = 8;
    TEXTS.set(key, map);
  }
  return TEXTS.get(key);
}

// Plates with the same text share one instanced plane. Tapping one that has a link opens it.
const OUT = 0.022 * Math.SQRT1_2;
function Plate({ items }) {
  const ref = useRef();
  useEffect(() => {
    const m = new THREE.Matrix4(), p = new THREE.Vector3();
    items.forEach((it, i) => ref.current.setMatrixAt(i, m.compose(p.set(it.x + OUT, 0.31 + (it.y || 0), it.z + OUT), FACE, ONE)));
    ref.current.instanceMatrix.needsUpdate = true;
    ref.current.computeBoundingSphere();
  }, [items]);
  const hover = (on) => (e) => { document.body.style.cursor = on && items[e.instanceId]?.link ? "pointer" : ""; };
  const open = (e) => {
    const link = items[e.instanceId]?.link;
    if (e.delta >= 6 || !link) return;
    e.stopPropagation();
    window.open(link, "_blank", "noopener,noreferrer");
  };
  return (
    <instancedMesh ref={ref} args={[null, null, items.length]} onClick={open} onPointerOver={hover(true)} onPointerOut={hover(false)}>
      <planeGeometry args={[W, H]} />
      <meshBasicMaterial map={plateTexture(items[0].sign)} />
    </instancedMesh>
  );
}

export function Plates({ plates }) {
  const groups = useMemo(() => {
    const g = {};
    for (const p of plates) (g[p.sign.join("|")] ||= []).push(p);
    return Object.values(g);
  }, [plates]);
  return groups.map((items) => <Plate key={items[0].sign.join("|")} items={items} />);
}
